import React, { useRef, useEffect, useState } from 'react';
import * as THREE from 'three';
import { disposeScene } from '../utils/ThreeCleanup';

const DisconnectVisualization: React.FC = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [phase, setPhase] = useState<number>(0);
  const [text, setText] = useState<string>('');
  const [isTerminalVisible, setIsTerminalVisible] = useState<boolean>(true);
  
  // Create refs to share state between useEffects
  const currentUpdateFnRef = useRef<((delta: number) => void) | null>(null);
  const phaseHandlersRef = useRef<{[key: number]: () => ((delta: number) => void) | void}>({});
  
  // Refs for text animation control
  const textAnimationRef = useRef<number | null>(null);
  const typingPositionRef = useRef<number>(0);
  const targetTextRef = useRef<string>('');
  
  // Improved text typing effect with RAF
  const animateText = (text: string, delay: number = 30, callback?: () => void) => {
    // Cancel any ongoing animation
    if (textAnimationRef.current !== null) {
      cancelAnimationFrame(textAnimationRef.current);
      textAnimationRef.current = null;
    }
    
    // Reset state
    setText('');
    typingPositionRef.current = 0;
    targetTextRef.current = text;
    
    // Typing animation frame
    const typeNextChar = () => {
      if (typingPositionRef.current < targetTextRef.current.length) {
        const nextPosition = Math.min(typingPositionRef.current + 1, targetTextRef.current.length);
        const currentText = targetTextRef.current.substring(0, nextPosition);
        setText(currentText);
        
        typingPositionRef.current = nextPosition;
        textAnimationRef.current = requestAnimationFrame(() => {
          // Add a deliberate delay between frames
          setTimeout(typeNextChar, delay);
        });
      } else {
        // Animation complete
        textAnimationRef.current = null;
        if (callback) callback();
      }
    };
    
    // Start animation
    textAnimationRef.current = requestAnimationFrame(typeNextChar);
  };
  
  useEffect(() => {
    if (!containerRef.current) return;
    
    let scene: THREE.Scene;
    let camera: THREE.PerspectiveCamera;
    let renderer: THREE.WebGLRenderer;
    let clock: THREE.Clock;
    let city: THREE.Group;
    let rainParticles: THREE.Points;
    let holographicSprites: THREE.Sprite[] = [];
    let starField: THREE.Points;
    let matrixRain: THREE.Points;
    
    // Initialize the scene
    const init = () => {
      // Setup scene
      scene = new THREE.Scene();
      scene.background = new THREE.Color(0x000000);
      scene.fog = new THREE.FogExp2(0x000000, 0.01);
      
      // Camera setup
      camera = new THREE.PerspectiveCamera(
        70, 
        containerRef.current!.clientWidth / containerRef.current!.clientHeight, 
        0.1, 
        1000
      );
      camera.position.z = 15;
      camera.position.y = 5;
      
      // Renderer
      renderer = new THREE.WebGLRenderer({ 
        antialias: true,
        alpha: true
      });
      renderer.setSize(containerRef.current!.clientWidth, containerRef.current!.clientHeight);
      renderer.setPixelRatio(window.devicePixelRatio);
      containerRef.current!.appendChild(renderer.domElement);
      
      // Clock for animations
      clock = new THREE.Clock();
      
      
    };
    
    // Phase 1: Terminal initialization
    const startPhase1 = () => {
      setPhase(1);
      
      // Type terminal text with improved animation
      animateText("[TYRELL_CORP v3.1]\nLoading synthetic memory stream...", 30, () => {
        // After delay, move to Phase 2
        setTimeout(() => {
          setIsTerminalVisible(false);
          startPhase2();
        }, 4000);
      });
    };
    
    // Phase 2: City immersion
    const startPhase2 = () => {
      setPhase(2);
      
      // Create city
      city = new THREE.Group();
      scene.add(city);
      
      // Create buildings
      for (let i = 0; i < 50; i++) {
        const width = Math.random() * 5 + 1;
        const height = Math.random() * 30 + 10;
        const depth = Math.random() * 5 + 1;
        
        const buildingGeometry = new THREE.BoxGeometry(width, height, depth);
        const buildingMaterial = new THREE.MeshPhongMaterial({
          color: 0x202020,
          emissive: new THREE.Color(Math.random() * 0.05, Math.random() * 0.05, Math.random() * 0.1),
          specular: new THREE.Color(0.1, 0.1, 0.1),
          shininess: 100,
          transparent: true,
          opacity: 0.9
        });
        
        const building = new THREE.Mesh(buildingGeometry, buildingMaterial);
        
        // Position buildings in a grid
        const gridSize = 100;
        const x = Math.random() * gridSize - gridSize/2;
        const z = Math.random() * gridSize - gridSize/2;
        
        building.position.set(x, height/2 - 5, z);
        
        // Add windows
        if (Math.random() > 0.3) {
          const windowsMaterial = new THREE.MeshBasicMaterial({
            color: 0x8ae6ff,
            transparent: true,
            opacity: 0.8 * Math.random()
          });
          
          // Create window pattern
          for (let y = 1; y < height - 1; y += 2) {
            for (let x = -width/2 + 0.5; x < width/2; x += 1) {
              for (let z = -depth/2 + 0.5; z < depth/2; z += 1) {
                if (Math.random() > 0.5) {
                  const windowGeometry = new THREE.BoxGeometry(0.5, 0.7, 0.1);
                  const windowMesh = new THREE.Mesh(windowGeometry, windowsMaterial);
                  windowMesh.position.set(x, y, z > 0 ? depth/2 + 0.05 : -depth/2 - 0.05);
                  building.add(windowMesh);
                }
              }
            }
          }
        }
        
        city.add(building);
      }
      
      // Add ground - reflective surface
      const groundGeometry = new THREE.PlaneGeometry(500, 500);
      const groundMaterial = new THREE.MeshStandardMaterial({
        color: 0x111111,
        metalness: 0.9,
        roughness: 0.1,
        envMapIntensity: 1.0
      });
      
      const ground = new THREE.Mesh(groundGeometry, groundMaterial);
      ground.rotation.x = -Math.PI / 2;
      ground.position.y = -5;
      scene.add(ground);
      
      // Rain effect
      const rainCount = 15000;
      const rainGeometry = new THREE.BufferGeometry();
      const rainPositions = new Float32Array(rainCount * 3);
      const rainVelocities = new Float32Array(rainCount);
      
      for (let i = 0; i < rainCount; i++) {
        rainPositions[i * 3] = Math.random() * 400 - 200;
        rainPositions[i * 3 + 1] = Math.random() * 500 - 250;
        rainPositions[i * 3 + 2] = Math.random() * 400 - 200;
        rainVelocities[i] = 0.1 + Math.random() * 0.3;
      }
      
      rainGeometry.setAttribute('position', new THREE.BufferAttribute(rainPositions, 3));
      rainGeometry.setAttribute('velocity', new THREE.BufferAttribute(rainVelocities, 1));
      
      const rainMaterial = new THREE.PointsMaterial({
        color: 0x3333ff,
        size: 0.2,
        transparent: true,
        opacity: 0.6,
        blending: THREE.AdditiveBlending
      });
      
      rainParticles = new THREE.Points(rainGeometry, rainMaterial);
      scene.add(rainParticles);
      
      // Add holographic sprites
      const spriteTexts = [
        "Memory Core: Active", 
        "Reality Cohesion: 97%", 
        "Neural Link: Established",
        "Identity: Confirmed",
        "Synthetic Dream Running"
      ];
      
      const createHoloSprite = (text: string, position: THREE.Vector3) => {
        // Create canvas for text rendering
        const canvas = document.createElement('canvas');
        canvas.width = 256;
        canvas.height = 128;
        const ctx = canvas.getContext('2d')!;
        
        // Draw text
        ctx.fillStyle = 'rgba(0, 0, 0, 0)';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.font = '16px monospace';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        
        // Glow effect
        ctx.shadowColor = '#00ffff';
        ctx.shadowBlur = 5;
        ctx.fillStyle = '#00ffff';
        ctx.fillText(text, canvas.width/2, canvas.height/2);
        
        // Create sprite
        const texture = new THREE.Texture(canvas);
        texture.needsUpdate = true;
        
        const material = new THREE.SpriteMaterial({
          map: texture,
          transparent: true,
          blending: THREE.AdditiveBlending
        });
        
        const sprite = new THREE.Sprite(material);
        sprite.position.copy(position);
        sprite.scale.set(10, 5, 1);
        
        scene.add(sprite);
        holographicSprites.push(sprite);
      };
      
      // Create holographic text sprites
      for (let i = 0; i < spriteTexts.length; i++) {
        const x = Math.random() * 80 - 40;
        const y = Math.random() * 20 + 5;
        const z = Math.random() * 80 - 40;
        
        createHoloSprite(spriteTexts[i], new THREE.Vector3(x, y, z));
      }
      
      // Add lights
      const ambientLight = new THREE.AmbientLight(0x222222);
      scene.add(ambientLight);
      
      const moonLight = new THREE.DirectionalLight(0x8ae6ff, 1);
      moonLight.position.set(0, 50, -30);
      scene.add(moonLight);
      
      // Neon lights
      const neonColors = [0xff0066, 0x00ffff, 0x9900ff, 0x00ff99];
      
      for (let i = 0; i < 20; i++) {
        const pointLight = new THREE.PointLight(
          neonColors[Math.floor(Math.random() * neonColors.length)], 
          0.5,
          20
        );
        const x = Math.random() * 100 - 50;
        const y = Math.random() * 10 + 2;
        const z = Math.random() * 100 - 50;
        
        pointLight.position.set(x, y, z);
        scene.add(pointLight);
      }
      
      // Start camera motion
      let cameraPath = new THREE.CatmullRomCurve3([
        new THREE.Vector3(0, 5, 15),
        new THREE.Vector3(-10, 8, 5),
        new THREE.Vector3(-20, 10, -10),
        new THREE.Vector3(-5, 15, -20),
        new THREE.Vector3(10, 8, -15),
        new THREE.Vector3(20, 6, 5),
        new THREE.Vector3(0, 5, 15),
      ]);
      
      let cameraPathProgress = 0;
      
      // Show text after a delay
      setTimeout(() => {
        setText('');
        animateText("You think this is real. But you never checked the source code.", 40);
        
        // Move to Phase 3 after some time
        setTimeout(() => {
          startPhase3();
        }, 10000);
      }, 5000);
      
      // Update function for this phase
      return (delta: number) => {
        // Update rain
        const positions = rainParticles.geometry.attributes.position.array as Float32Array;
        const velocities = rainParticles.geometry.attributes.velocity.array as Float32Array;
        
        for (let i = 0; i < rainCount; i++) {
          positions[i * 3 + 1] -= velocities[i] * (20 * delta);
          
          // Reset rain to top when it hits the ground
          if (positions[i * 3 + 1] < -5) {
            positions[i * 3 + 1] = 50;
          }
        }
        
        rainParticles.geometry.attributes.position.needsUpdate = true;
        
        // Update camera path
        cameraPathProgress += 0.05 * delta;
        if (cameraPathProgress > 1) cameraPathProgress = 0;
        
        const camPos = cameraPath.getPoint(cameraPathProgress);
        camera.position.copy(camPos);
        
        // Look at center
        camera.lookAt(0, 0, 0);
        
        // Update holographic sprites
        holographicSprites.forEach(sprite => {
          // Slowly rotate to face camera
          sprite.lookAt(camera.position);
          
          // Add floating effect
          const time = Date.now() * 0.001;
          sprite.position.y += Math.sin(time + sprite.position.x) * 0.01;
        });
      };
    };
    
    // Phase 3: Disconnection
    const startPhase3 = () => {
      setPhase(3);
      
      // Type new message with improved animation
      setText('');
      animateText("> reality: undefined\n> consciousness overflow\n> /disconnecting user...", 40);
      
      // Start distorting scene
      const distortScene = (progress: number) => {
        // Distort buildings
        city.children.forEach((building: THREE.Object3D, _i) => {
          if (building instanceof THREE.Mesh) {
            // Randomly distort vertices
            if (Math.random() > 0.98) {
              const positions = (building.geometry as THREE.BufferGeometry).attributes.position.array as Float32Array;
              
              for (let j = 0; j < positions.length; j += 3) {
                // Increasingly severe distortion
                const distortFactor = progress * 0.5;
                positions[j] += (Math.random() - 0.5) * distortFactor;
                positions[j+1] += (Math.random() - 0.5) * distortFactor;
                positions[j+2] += (Math.random() - 0.5) * distortFactor;
              }
              
              (building.geometry as THREE.BufferGeometry).attributes.position.needsUpdate = true;
            }
            
            // Distort material color
            if (building.material instanceof THREE.MeshPhongMaterial) {
              if (Math.random() > 0.95) {
                building.material.color.set(
                  Math.random() > 0.5 ? 0xff0000 : 0x0000ff
                );
              }
              
              // Randomly change opacity
              building.material.opacity = Math.max(0.3, 1 - progress * Math.random());
            }
          }
        });
        
        // Distort rain color
        if (rainParticles.material instanceof THREE.PointsMaterial) {
          const hue = 0.6 + Math.sin(Date.now() * 0.001) * 0.2;
          rainParticles.material.color.setHSL(hue, 1, 0.5);
          
          // Make rain particles more erratic
          const positions = rainParticles.geometry.attributes.position.array as Float32Array;
          const velocities = rainParticles.geometry.attributes.velocity.array as Float32Array;
          
          for (let i = 0; i < positions.length/3; i++) {
            if (Math.random() > 0.99) {
              positions[i*3] += (Math.random() - 0.5) * 5 * progress;
              positions[i*3+2] += (Math.random() - 0.5) * 5 * progress;
              velocities[i] = Math.random() * 0.3 * (1 + progress);
            }
          }
          
          rainParticles.geometry.attributes.position.needsUpdate = true;
        }
        
        // Distort holographic text
        holographicSprites.forEach((sprite, _i) => {
          if (Math.random() > 0.95) {
            sprite.visible = !sprite.visible;
          }
          
          // Move around erratically
          if (Math.random() > 0.9) {
            sprite.position.x += (Math.random() - 0.5) * progress * 2;
            sprite.position.y += (Math.random() - 0.5) * progress * 2;
            sprite.position.z += (Math.random() - 0.5) * progress * 2;
          }
        });
        
        // Distort camera
        if (Math.random() > 0.9) {
          camera.position.x += (Math.random() - 0.5) * progress * 0.2;
          camera.position.y += (Math.random() - 0.5) * progress * 0.2;
          camera.rotation.z = (Math.random() - 0.5) * progress * 0.05;
        }
        
      };
      
      // Create countdown for disconnection
      let disconnectProgress = 0;
      const disconnectInterval = setInterval(() => {
        disconnectProgress += 0.01;
        
        // Increase distortion
        distortScene(disconnectProgress);
        
        if (disconnectProgress >= 1) {
          clearInterval(disconnectInterval);
          startPhase4();
        }
      }, 100);
      
      // Create update function for this phase
      return (delta: number) => {
        // Continue running the city scene with increasing distortion
        // Rain still falls
        const positions = rainParticles.geometry.attributes.position.array as Float32Array;
        const velocities = rainParticles.geometry.attributes.velocity.array as Float32Array;
        
        for (let i = 0; i < positions.length/3; i++) {
          positions[i * 3 + 1] -= velocities[i] * (20 * delta);
          
          // Reset rain to top when it hits the ground, with increasing chaos
          if (positions[i * 3 + 1] < -5) {
            positions[i * 3 + 1] = 50 + Math.random() * 50 * disconnectProgress;
            positions[i * 3] = Math.random() * 400 - 200;
            positions[i * 3 + 2] = Math.random() * 400 - 200;
          }
        }
        
        rainParticles.geometry.attributes.position.needsUpdate = true;
      };
    };
    
    // Phase 4: Extinction
    const startPhase4 = () => {
      setPhase(4);
      
      // Clear scene
      while(scene.children.length > 0) { 
        scene.remove(scene.children[0]); 
      }
      
      // Create starfield
      const starsCount = 2000;
      const starsGeometry = new THREE.BufferGeometry();
      const starPositions = new Float32Array(starsCount * 3);
      
      for (let i = 0; i < starsCount * 3; i += 3) {
        starPositions[i] = (Math.random() - 0.5) * 2000;
        starPositions[i + 1] = (Math.random() - 0.5) * 2000;
        starPositions[i + 2] = (Math.random() - 0.5) * 2000;
      }
      
      starsGeometry.setAttribute('position', new THREE.BufferAttribute(starPositions, 3));
      
      const starMaterial = new THREE.PointsMaterial({
        color: 0xffffff,
        size: 0.8,
        transparent: true,
        opacity: 0.8
      });
      
      starField = new THREE.Points(starsGeometry, starMaterial);
      scene.add(starField);
      
      // Create matrix rain
      const matrixRainCount = 1000;
      const matrixRainGeometry = new THREE.BufferGeometry();
      const matrixRainPositions = new Float32Array(matrixRainCount * 3);
      const matrixRainVelocities = new Float32Array(matrixRainCount);
      
      for (let i = 0; i < matrixRainCount; i++) {
        matrixRainPositions[i * 3] = (Math.random() - 0.5) * 100;
        matrixRainPositions[i * 3 + 1] = Math.random() * 200;
        matrixRainPositions[i * 3 + 2] = (Math.random() - 0.5) * 100;
        matrixRainVelocities[i] = 2 + Math.random() * 5;
      }
      
      matrixRainGeometry.setAttribute('position', new THREE.BufferAttribute(matrixRainPositions, 3));
      matrixRainGeometry.setAttribute('velocity', new THREE.BufferAttribute(matrixRainVelocities, 1));
      
      const matrixRainMaterial = new THREE.PointsMaterial({
        color: 0x00ff00,
        size: 0.5,
        transparent: true,
        opacity: 0.8,
        blending: THREE.AdditiveBlending
      });
      
      matrixRain = new THREE.Points(matrixRainGeometry, matrixRainMaterial);
      scene.add(matrixRain);
      
      // Final text after brief delay
      setTimeout(() => {
        setText('');
        animateText("\"If only you could see what I've seen with your eyes.\"", 50, () => {
          // Start fadeout after delay
          setTimeout(() => {
            // Fade to black
            const fadeOut = setInterval(() => {
              if (starMaterial.opacity > 0.01) {
                starMaterial.opacity -= 0.01;
                matrixRainMaterial.opacity -= 0.01;
              } else {
                clearInterval(fadeOut);
                // Clear text after complete fade
                setTimeout(() => {
                  setText('');
                }, 3000);
              }
            }, 50);
          }, 5000);
        });
      }, 2000);
      
      // Update function for this phase
      return (delta: number) => {
        // Rotate starfield slowly
        if (starField) {
          starField.rotation.y += 0.001;
          starField.rotation.x += 0.0005;
        }
        
        // Animate matrix rain
        if (matrixRain) {
          const positions = matrixRain.geometry.attributes.position.array as Float32Array;
          const velocities = matrixRain.geometry.attributes.velocity.array as Float32Array;
          
          for (let i = 0; i < matrixRainCount; i++) {
            positions[i * 3 + 1] -= velocities[i] * delta;
            
            // Reset particles when they fall out of view
            if (positions[i * 3 + 1] < -50) {
              positions[i * 3 + 1] = 150;
              positions[i * 3] = (Math.random() - 0.5) * 100;
              positions[i * 3 + 2] = (Math.random() - 0.5) * 100;
            }
          }
          
          matrixRain.geometry.attributes.position.needsUpdate = true;
        }
      };
    };
    
    // Initialize and start the animation
    init();
    
    // Phase tracking and update function
    // Instead of a local variable, use the ref
    
    // Set up phase handlers
    phaseHandlersRef.current = {
      1: startPhase1,
      2: startPhase2,
      3: startPhase3,
      4: startPhase4
    };
    
    // Start the first phase after a short delay
    setTimeout(() => {
      startPhase1();
    }, 1000);
    
    // Animation loop
    let animationFrameId = 0;
    const animate = () => {
      animationFrameId = requestAnimationFrame(animate);

      const delta = clock.getDelta();
      
      // Call the current phase's update function
      if (currentUpdateFnRef.current) {
        currentUpdateFnRef.current(delta);
      }
      
      // Render scene
      renderer.render(scene, camera);
    };
    
    // Start animation
    animate();
    
    // Window resize handler
    const handleResize = () => {
      if (!containerRef.current) return;
      
      const width = containerRef.current.clientWidth;
      const height = containerRef.current.clientHeight;
      
      camera.aspect = width / height;
      camera.updateProjectionMatrix();
      
      renderer.setSize(width, height);
    };
    
    window.addEventListener('resize', handleResize);
    
    // Cleanup
    return () => {
      cancelAnimationFrame(animationFrameId);
      window.removeEventListener('resize', handleResize);

      // Remove renderer
      if (containerRef.current && containerRef.current.contains(renderer.domElement)) {
        containerRef.current.removeChild(renderer.domElement);
      }
      disposeScene(scene, renderer);

      // Cancel any ongoing text animations
      if (textAnimationRef.current !== null) {
        cancelAnimationFrame(textAnimationRef.current);
      }
    };
  }, []);
  
  // Move the nested useEffect to the component top level
  useEffect(() => {
    if (phase > 0 && phaseHandlersRef.current[phase]) {
      currentUpdateFnRef.current = phaseHandlersRef.current[phase]() || null;
    }
  }, [phase]);
  
  // Cleanup animation on unmount
  useEffect(() => {
    return () => {
      if (textAnimationRef.current !== null) {
        cancelAnimationFrame(textAnimationRef.current);
      }
    };
  }, []);
  
  return (
    <div className="w-full h-full">
      <div className="relative w-full h-full bg-black overflow-hidden" ref={containerRef}>
        {isTerminalVisible && (
          <div className="absolute inset-0 flex items-center justify-center bg-black z-10">
            <div className="terminal-text text-[var(--terminal-text)] p-8 font-mono whitespace-pre-line">
              {text}
              <span className="cursor-blink">█</span>
            </div>
          </div>
        )}
        
        {!isTerminalVisible && (
          <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
            <div className="terminal-text text-center p-8 text-[var(--terminal-text)] font-mono whitespace-pre-line max-w-2xl">
              {text}
              {text && <span className="cursor-blink">█</span>}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DisconnectVisualization;